import React, { useState, useMemo, useEffect } from 'react';
import {
  AlertTriangle,
  DollarSign,
  Eye,
  BarChart3,
  Clock,
  CheckCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { formatCurrency, formatDate, formatCompactCurrency, maturityPayoutsAPI, maturityDistributionRequestsAPI, default as api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const MaturityManagement = () => {
  const { getDisplayName, user } = useAuth();
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('received');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bondTypeFilter, setBondTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // State for real data
  const [payouts, setPayouts] = useState([]);
  const [payoutStats, setPayoutStats] = useState(null);
  const [allPayoutCount, setAllPayoutCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // State for maturity distribution requests (investor approvals)
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsError, setRequestsError] = useState(null);
  const [approvingRequestId, setApprovingRequestId] = useState(null);

  const currentUserId = user?._id || user?.id;
  const currentLei =
    user?.lei ||
    user?.corporateInfo?.lei ||
    null;

  const getNumericValue = (value) => {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof value === 'object') {
      if ('$numberDecimal' in value) {
        const parsed = Number(value.$numberDecimal);
        return Number.isFinite(parsed) ? parsed : 0;
      }
      if ('$numberDouble' in value) {
        const parsed = Number(value.$numberDouble);
        return Number.isFinite(parsed) ? parsed : 0;
      }
    }
    return 0;
  };

  const getUserIdValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value._id || value.id || null;
  };

  const canApproveL2 = (request) => {
    if (request.status !== 'pending_l2') return true;
    const l1Approver = getUserIdValue(request.l1ApprovedBy);
    if (!l1Approver || !currentUserId) return true;
    return l1Approver.toString() !== currentUserId.toString();
  };

  const getRequestActionLabel = (request) =>
    request.status === 'pending_l2' ? 'Approve L2' : 'Approve';

  const fetchRequests = async () => {
    try {
      setRequestsLoading(true);
      setRequestsError(null);
      const response = await maturityDistributionRequestsAPI.getMyRequests({ status: 'all' });
      const data = response.data?.data || response.data || [];
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching maturity distribution requests:', err);
      setRequests([]);
      setRequestsError(err?.response?.data?.message || err.message || 'Failed to fetch distribution requests');
    } finally {
      setRequestsLoading(false);
    }
  };

  // Filter and search logic
  const filteredPayouts = useMemo(() => {
    const filtered = payouts.filter(payout => {
      // Support both bondSnapshot (from backend enrichment) and bondId (populated reference)
      const bondName = payout.bondSnapshot?.bondName || payout.bondId?.bondName || '';
      const isin = payout.bondSnapshot?.isin || payout.bondId?.isin || '';
      const issuer = payout.bondSnapshot?.issuer || 
        payout.bondId?.issuer?.corporateInfo?.companyName || 
        payout.bondId?.issuer?.email || 
        '';
      
      const matchesSearch = !searchTerm || 
        bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        isin.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issuer.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || payout.status === statusFilter;
      
      // Support both bondSnapshot.bondType and bondId.productType
      const bondType = payout.bondSnapshot?.bondType || 
        (payout.bondId?.productType === 'Sukuk' ? 'Islamic' : 'Traditional');
      const matchesBondType = bondTypeFilter === 'all' || bondType === bondTypeFilter;

      return matchesSearch && matchesStatus && matchesBondType;
    });
    
    // Debug: Log filtering results
    if (payouts.length > 0 && filtered.length === 0) {
      console.log('All payouts filtered out:', {
        totalPayouts: payouts.length,
        searchTerm,
        statusFilter,
        bondTypeFilter,
        samplePayout: payouts[0]
      });
    }
    
    return filtered;
  }, [payouts, searchTerm, statusFilter, bondTypeFilter]);

  const filteredRequests = useMemo(() => {
    const actionable = requests.filter((request) =>
      request.status === 'pending' || request.status === 'pending_l2'
    );

    if (!searchTerm) {
      return actionable;
    }

    const keyword = searchTerm.toLowerCase();

    return actionable.filter((request) => {
      const bondName = request.bondId?.bondName?.toLowerCase() || '';
      const isin = request.bondId?.isin?.toLowerCase() || '';
      const status = (request.status || '').toLowerCase();
      return (
        bondName.includes(keyword) ||
        isin.includes(keyword) ||
        status.includes(keyword)
      );
    });
  }, [requests, searchTerm]);

  const combinedRows = useMemo(() => {
    const requestRows = filteredRequests.map((r) => ({
      ...r,
      _rowKind: 'request'
    }));
    const payoutRows = filteredPayouts.map((p) => ({
      ...p,
      _rowKind: 'payout'
    }));
    return [...requestRows, ...payoutRows];
  }, [filteredRequests, filteredPayouts]);

  const totalPages = pagination.totalPages || 1;
  const currentData = filteredPayouts;
  const pageStartOrdinal = (pagination.page - 1) * pagination.limit + 1;
  const pageEndOrdinal = pageStartOrdinal + currentData.length - 1;

  // Fetch payouts data
  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          page: currentPage,
          limit: itemsPerPage,
          ...(statusFilter !== 'all' && { status: statusFilter }),
          ...(bondTypeFilter !== 'all' && { bondType: bondTypeFilter })
        };

        const response = await maturityPayoutsAPI.getPayouts(params);

        if (response.data && response.data.success) {
          const responseData = response.data.data;
          let fetchedPayouts = responseData.payouts || [];

          // Enrich payouts with wallet / bank account information if missing
          try {
            const walletResp = await api.get('/api/wallets/my-wallet');
            const walletWrapper = walletResp?.data?.data || walletResp?.data || {};
            const walletObj = walletWrapper.wallet || walletWrapper;
            let investorWalletAddress = walletObj?.walletAddress;
            let bankAccountType = walletObj?.bankAccountDetails?.accountType;

            if (!bankAccountType && investorWalletAddress) {
              try {
                const detailResp = await api.get(`/api/wallets/address/${investorWalletAddress}`);
                const detailData = detailResp?.data?.wallet || detailResp?.data?.data?.wallet || detailResp?.data;
                bankAccountType = detailData?.bankAccountDetails?.accountType || bankAccountType;
              } catch (inner) {
                // ignore
              }
            }

            fetchedPayouts = fetchedPayouts.map(p => {
              const resolvedAddress = p.walletAddress || investorWalletAddress || null;
              const resolvedBankType = p.bankAccountType || bankAccountType || null;
              return {
                ...p,
                walletAddress: resolvedAddress,
                bankAccountType: resolvedBankType
              };
            });
          } catch (e) {
            // Silent enrichment failure
          }
          setPayouts(fetchedPayouts);
          setPagination(responseData.pagination || {
            page: currentPage,
            limit: itemsPerPage,
            total: 0,
            totalPages: 0
          });
        } else {
          setError('Failed to fetch maturity payouts');
        }
      } catch (error) {
        console.error('Error fetching maturity payouts:', error);
        setError('Failed to fetch maturity payouts');
      } finally {
        setLoading(false);
      }
    };

    const fetchPayoutStats = async () => {
      try {
        const response = await maturityPayoutsAPI.getPayoutStats();
        if (response.data && response.data.success) {
          setPayoutStats(response.data.data);
        }
        try {
          const latestResp = await maturityPayoutsAPI.getPayouts({ page: 1, limit: 1 });
          const listWrapper = latestResp?.data?.data || {};
          const list = listWrapper.payouts || [];
          if (typeof listWrapper?.pagination?.total === 'number') {
            setAllPayoutCount(listWrapper.pagination.total);
          }
        } catch (e) {
          // ignore
        }
      } catch (error) {
        console.error('Error fetching maturity payout stats:', error);
      }
    };

    fetchPayouts();
    fetchRequests();
    fetchPayoutStats();
  }, [currentPage, itemsPerPage, statusFilter, bondTypeFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, bondTypeFilter]);

  const visibleTotalAmount = useMemo(() => currentData.reduce((sum, p) => sum + (p.paymentAmount || 0), 0), [currentData]);
  const visibleEvents = currentData.length;
  const visibleAverageAmount = visibleEvents > 0 ? visibleTotalAmount / visibleEvents : 0;

  const overallTotalAmount = typeof payoutStats?.totalAmount === 'number' ? payoutStats.totalAmount : visibleTotalAmount;
  const overallTotalEvents = (typeof allPayoutCount === 'number')
    ? allPayoutCount
    : (typeof payoutStats?.totalPayouts === 'number'
      ? payoutStats.totalPayouts
      : (pagination?.total || visibleEvents));
  const completedTotalAmount = typeof payoutStats?.totalCompletedAmount === 'number' ? payoutStats.totalCompletedAmount : overallTotalAmount;
  const completedTotalEvents = typeof payoutStats?.totalCompletedPayouts === 'number' ? payoutStats.totalCompletedPayouts : overallTotalEvents;
  const overallAverageAmount = typeof payoutStats?.averageAmount === 'number' ? payoutStats.averageAmount : visibleAverageAmount;

  const keyMetrics = [
    {
      name: 'Total Received Maturity Payouts',
      value: formatCompactCurrency(completedTotalAmount || overallTotalAmount),
      change: payoutStats ? 'Completed (all time)' : 'Loading…',
      icon: DollarSign,
      color: 'success',
      secondary: (overallTotalAmount !== completedTotalAmount ? `All (any status): ${formatCompactCurrency(overallTotalAmount)}` : null)
        || (visibleTotalAmount !== completedTotalAmount ? `Visible: ${formatCompactCurrency(visibleTotalAmount)}` : null)
    },
    {
      name: 'Total Maturity Events',
      value: String(overallTotalEvents),
      change: payoutStats ? 'All time count' : 'Loading…',
      icon: CheckCircle,
      color: 'primary',
      secondary: visibleEvents !== overallTotalEvents ? `Visible: ${visibleEvents}` : null
    },
    {
      name: 'Average Maturity Amount',
      value: formatCompactCurrency(overallAverageAmount),
      change: payoutStats ? 'Global average' : 'Loading…',
      icon: BarChart3,
      color: 'info',
      secondary: visibleAverageAmount !== overallAverageAmount ? `Visible: ${formatCompactCurrency(visibleAverageAmount)}` : null
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { label: 'Scheduled', class: 'badge-info' },
      completed: { label: 'Completed', class: 'badge-success' },
      pending: { label: 'Pending', class: 'badge-warning' },
      failed: { label: 'Failed', class: 'badge-error' }
    };
    const config = statusConfig[status] || { label: status, class: 'badge-secondary' };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const getRequestStatusBadge = (status) => {
    if (status === 'approved') {
      return <span className="badge badge-success">Approved</span>;
    }
    if (status === 'pending_l2') {
      return <span className="badge badge-warning">Pending L2</span>;
    }
    if (status === 'pending') {
      return <span className="badge badge-warning">Pending L1</span>;
    }
    if (status === 'rejected') {
      return <span className="badge badge-error">Rejected</span>;
    }
    return <span className="badge badge-secondary">{status}</span>;
  };

  const getBondTypeDisplay = (bondType) => {
    return bondType === 'Islamic' ? 'Sukuk' : 'Conventional Bond';
  };

  const handleViewDetails = (payout) => {
    setSelectedPayout(payout);
    setShowDetails(true);
  };

  const handleApproveRequest = async (request) => {
    try {
      setApprovingRequestId(request._id);
      if (request.status === 'pending') {
        await maturityDistributionRequestsAPI.approveRequestL1(request._id);
      } else if (request.status === 'pending_l2') {
        await maturityDistributionRequestsAPI.approveRequestL2(request._id);
      }
      await fetchRequests();
      try {
        const params = {
          page: currentPage,
          limit: itemsPerPage,
          ...(statusFilter !== 'all' && { status: statusFilter }),
          ...(bondTypeFilter !== 'all' && { bondType: bondTypeFilter })
        };
        const response = await maturityPayoutsAPI.getPayouts(params);
        if (response.data && response.data.success) {
          const responseData = response.data.data;
          let fetchedPayouts = responseData.payouts || [];

          try {
            const walletResp = await api.get('/api/wallets/my-wallet');
            const walletWrapper = walletResp?.data?.data || walletResp?.data || {};
            const walletObj = walletWrapper.wallet || walletWrapper;
            let investorWalletAddress = walletObj?.walletAddress;
            let bankAccountType = walletObj?.bankAccountDetails?.accountType;

            if (!bankAccountType && investorWalletAddress) {
              try {
                const detailResp = await api.get(`/api/wallets/address/${investorWalletAddress}`);
                const detailData = detailResp?.data?.wallet || detailResp?.data?.data?.wallet || detailResp?.data;
                bankAccountType = detailData?.bankAccountDetails?.accountType || bankAccountType;
              } catch (inner) {
                // ignore
              }
            }

            fetchedPayouts = fetchedPayouts.map(p => {
              const resolvedAddress = p.walletAddress || investorWalletAddress || null;
              const resolvedBankType = p.bankAccountType || bankAccountType || null;
              return {
                ...p,
                walletAddress: resolvedAddress,
                bankAccountType: resolvedBankType
              };
            });
          } catch (e) {
            // ignore enrichment errors
          }

          setPayouts(fetchedPayouts);
          setPagination(responseData.pagination || {
            page: currentPage,
            limit: itemsPerPage,
            total: 0,
            totalPages: 0
          });
        }
      } catch (err) {
        console.error('Error refreshing payouts after approval:', err);
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.message || err.message || 'Failed to approve request.');
    } finally {
      setApprovingRequestId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Page Header */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Maturity Management</h1>
            <p className="mt-1 text-secondary-600">
              Monitor your upcoming maturity payments
            </p>
          </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {keyMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.name} className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 p-3 rounded-lg bg-${metric.color}-100`}>
                    <Icon className={`h-6 w-6 text-${metric.color}-600`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">
                      {metric.name}
                    </p>
                    <p className="text-2xl font-bold text-secondary-900">
                      {metric.value}
                    </p>
                    <p className="text-xs text-secondary-500">
                      {metric.change}
                    </p>
                    {metric.secondary && (
                      <p className="text-[10px] text-secondary-400 mt-0.5">{metric.secondary}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="card lg:col-span-3">
          <div className="card-body">
            <div className="relative">
              <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-dark-gray" />
              <input
                type="text"
                placeholder="Search maturity payouts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 form-input"
              />
            </div>
          </div>
        </div>

        <div className="card lg:col-span-1">
          <div className="card-body">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <div className="card lg:col-span-1">
          <div className="card-body">
            <select
              value={bondTypeFilter}
              onChange={(e) => setBondTypeFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">All Types</option>
              <option value="Islamic">Sukuk (Islamic)</option>
              <option value="Traditional">Conventional</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests + Payouts Table */}
      <div className="card">
        <div className="card-header">
          <h3>Maturity Management</h3>
        </div>

        <div className="p-0 card-body">
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {combinedRows.length === 0 ? 0 : pageStartOrdinal}-{combinedRows.length === 0 ? 0 : (pageStartOrdinal + combinedRows.length - 1)} of {pagination.total} maturity payouts
              </p>
              <p className="text-sm text-gray-600" title="All-time total maturity payout amount">
                All-Time Value: {formatCurrency(overallTotalAmount)}
                {visibleTotalAmount !== overallTotalAmount && (
                  <span className="ml-2 text-xs text-gray-500" title="Sum of amounts in current filtered page">Visible: {formatCurrency(visibleTotalAmount)}</span>
                )}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Bond Details</th>
                  <th className="table-header-cell">Type &amp; Amount</th>
                  <th className="table-header-cell">LEI</th>
                  <th className="table-header-cell">
                    Maturity / Received Date
                  </th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading || requestsLoading ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center">
                      <LoadingSpinner />
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center">
                      <div className="text-red-500">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-300" />
                        <p className="text-lg font-medium">Error loading maturity payouts</p>
                        <p className="text-sm">{error}</p>
                      </div>
                    </td>
                  </tr>
                ) : requestsError ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center">
                      <div className="text-red-500">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-300" />
                        <p className="text-lg font-medium">Error loading requests</p>
                        <p className="text-sm">{requestsError}</p>
                      </div>
                    </td>
                  </tr>
                ) : combinedRows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center">
                      <div className="text-gray-500">
                        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">No requests or maturity payments found</p>
                        <p className="text-sm">Try adjusting your search or filter criteria</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  combinedRows.map((row) => {
                    const isRequest = row._rowKind === 'request';
                    const key = isRequest ? `request-${row._id}` : `payout-${row.id || row._id}`;
                    const bondTypeFromBond =
                      row.bondId?.productType === 'Sukuk' ? 'Islamic' : 'Traditional';
                    const effectiveBondType = row.bondSnapshot?.bondType || row.bondType || bondTypeFromBond;

                    return (
                      <tr key={key} className="transition-colors border-b border-secondary-100 hover:bg-secondary-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="mb-1 text-sm font-semibold leading-5 text-dark-gray-900">
                            {row.bondSnapshot?.bondName || row.bondName || row.bondId?.bondName || 'Unknown Bond'}
                          </div>
                          <div className="text-xs text-secondary-500">
                            ISIN: {row.bondSnapshot?.isin || row.isin || row.bondId?.isin || 'N/A'}
                          </div>
                          <div className="text-xs text-secondary-500">
                            {getBondTypeDisplay(effectiveBondType)}
                            {(row.bondSnapshot?.issuer || row.issuer || row.bondId?.issuer) && ` • ${row.bondSnapshot?.issuer || row.issuer || row.bondId?.issuer}`}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center mb-1">
                          <CheckCircle className="w-4 h-4 text-success-600" />
                          <span className="ml-2 text-sm font-medium text-dark-gray-900">
                            {isRequest
                              ? formatCurrency(getNumericValue(row.amount))
                              : formatCurrency(row.paymentAmount)}
                          </span>
                        </div>
                        <div className="text-xs text-secondary-500">
                          Redemption Payment
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-mono text-sm text-secondary-900">
                          {(row.lei || row.holderSnapshot?.lei || (row._rowKind === 'request' ? currentLei : null)) ? (
                            <span className="text-xs">
                              {row.lei || row.holderSnapshot?.lei || (row._rowKind === 'request' ? currentLei : null)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-secondary-900">
                          {formatDate(row.maturityDate || row.paymentDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {isRequest ? getRequestStatusBadge(row.status) : getStatusBadge(row.status)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {isRequest ? (
                            <>
                              {!(row.status === 'pending_l2' && !canApproveL2(row)) && (
                                <button
                                  onClick={() => handleApproveRequest(row)}
                                  disabled={approvingRequestId === row._id}
                                  className="btn-primary btn-sm"
                                >
                                  {approvingRequestId === row._id
                                    ? 'Approving...'
                                    : getRequestActionLabel(row)}
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              onClick={() => handleViewDetails(row)}
                              className="inline-flex items-center justify-center w-8 h-8 transition-colors rounded-lg text-primary-600 hover:text-secondary-700"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-3 border-t bg-secondary-50 border-light-gray">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-secondary-500">
                  <span>
                    Showing {pageStartOrdinal} to {pageEndOrdinal} of {pagination.total} results
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${page === currentPage
                          ? 'bg-primary-600 text-white'
                          : 'bg-white border border-secondary-300 text-secondary-700 hover:bg-secondary-50'
                        }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payout Summary */}
      <div className="card">
        <div className="card-header">
          <h3>Maturity Payout Summary</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="p-4 text-center rounded-lg bg-success-50" title="Sum of completed maturity payout amounts (actually received)">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-success-600" />
              <div className="text-2xl font-bold text-success-900">
                {formatCompactCurrency(completedTotalAmount || overallTotalAmount)}
              </div>
              <div className="text-sm text-success-600">
                Total Received (Completed)
              </div>
              {overallTotalAmount !== completedTotalAmount && (
                <div className="mt-1 text-xs text-success-700" title="Sum of all maturity payout amounts (any status, may include pending/scheduled)">
                  All (any status): {formatCompactCurrency(overallTotalAmount)}
                </div>
              )}
              {visibleTotalAmount !== completedTotalAmount && (
                <div className="mt-1 text-xs text-success-700" title="Sum of amounts in current filtered list">
                  Visible: {formatCompactCurrency(visibleTotalAmount)}
                </div>
              )}
            </div>
            <div className="p-4 text-center rounded-lg bg-info-50">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-info-600" />
              <div className="text-2xl font-bold text-info-900">
                {overallTotalEvents}
              </div>
              <div className="text-sm text-info-600">
                Total Maturity Events
              </div>
              {visibleEvents !== overallTotalEvents && (
                <div className="mt-1 text-xs text-info-700" title="Count of maturity payouts in the currently visible list">
                  Visible: {visibleEvents}
                </div>
              )}
            </div>
            <div className="p-4 text-center rounded-lg bg-warning-50">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 text-warning-600" />
              <div className="text-2xl font-bold text-warning-900">
                {formatCompactCurrency(overallAverageAmount)}
              </div>
              <div className="text-sm text-warning-600">
                Average Maturity Amount
              </div>
              {visibleAverageAmount !== overallAverageAmount && (
                <div className="mt-1 text-xs text-warning-700" title="Average of maturity payouts in the currently visible list">
                  Visible: {formatCompactCurrency(visibleAverageAmount)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payout Details Modal */}
      {showDetails && selectedPayout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-2xl mx-4 bg-white rounded-lg">
            <div className="p-6 pb-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Maturity Payout Details</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" style={{ color: '#9ca3af' }} />
                </button>
              </div>
              <div className="w-full mt-4 border-b border-light-gray "></div>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-dark-gray">Bond Name:</label>
                  <div className="mt-1 text-sm text-dark-gray">
                    {selectedPayout.bondSnapshot?.bondName || selectedPayout.bondName}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-dark-gray">ISIN:</label>
                  <div className="mt-1 font-mono text-sm text-dark-gray">
                    {selectedPayout.bondSnapshot?.isin || selectedPayout.isin}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-dark-gray">Bond Type:</label>
                  <div className="mt-1 text-sm text-dark-gray">
                    {getBondTypeDisplay(selectedPayout.bondSnapshot?.bondType || selectedPayout.bondType)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-dark-gray">Payout Type:</label>
                  <div className="flex items-center mt-1">
                    <CheckCircle className="w-4 h-4 text-success-600" />
                    <span className="ml-2 text-sm text-dark-gray">
                      Redemption Payment
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-dark-gray">Amount:</label>
                  <div className="mt-1 text-sm text-dark-gray">
                    {formatCurrency(selectedPayout.paymentAmount)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-dark-gray">
                    Maturity Date:
                  </label>
                  <div className="mt-1 text-sm text-dark-gray">
                    {formatDate(selectedPayout.maturityDate)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-dark-gray">Status:</label>
                  <div className="mt-1">
                    {getStatusBadge(selectedPayout.status)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default MaturityManagement;

